import { useCallback, useEffect, useState } from "react";
import { Library, Search, Trash2, Copy, Download, Pencil, GitBranch, Loader2, Send, FileCode, Clock, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SpecPreview } from "@/components/CxAlloyPanel";
import { confirmDialog, promptDialog } from "@/lib/dialogs";
import { downloadBlob, downloadViaUrl, safeName } from "@/lib/download";

export default function ScriptsPanel({ project, selectedDocs, refreshKey }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [script, setScript] = useState(null);
  const [versionIdx, setVersionIdx] = useState(0);
  const [iteratePrompt, setIteratePrompt] = useState("");
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.listScripts(project.id, q || undefined);
      setItems(list);
      if (list.length && !activeId) {
        setActiveId(list[0].id);
      } else if (!list.length) {
        setActiveId(null);
        setScript(null);
      }
    } catch {
      toast.error("Failed to load scripts");
    }
  }, [project.id, q, activeId]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    api
      .getScript(activeId)
      .then((s) => {
        if (!alive) return;
        setScript(s);
        setVersionIdx(s.versions.length - 1);
      })
      .catch(() => toast.error("Failed to load script"));
    return () => {
      alive = false;
    };
  }, [activeId]);

  const remove = async (e, s) => {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: `Delete script "${s.name}"?`,
      message: "All versions of this script will be permanently removed.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteScript(s.id);
      toast.success("Script deleted");
      if (s.id === activeId) setActiveId(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const rename = async (e, s) => {
    e.stopPropagation();
    const name = await promptDialog({
      title: "Rename script",
      defaultValue: s.name,
      confirmLabel: "Rename",
    });
    if (!name || name.trim() === s.name) return;
    try {
      await api.renameScript(s.id, name.trim());
      toast.success("Renamed");
      await load();
      if (s.id === activeId) {
        const fresh = await api.getScript(s.id);
        setScript(fresh);
      }
    } catch {
      toast.error("Rename failed");
    }
  };

  const iterate = async () => {
    if (!iteratePrompt.trim() || !script) return;
    setWorking(true);
    try {
      const isCx = script.language === "cxalloy";
      const res = isCx
        ? await api.cxalloyIterate(script.id, {
            prompt: iteratePrompt,
            document_ids: selectedDocs,
          })
        : await api.iterateScript(script.id, {
            prompt: iteratePrompt,
            document_ids: selectedDocs,
          });
      setScript(res.script);
      setVersionIdx(res.script.versions.length - 1);
      setIteratePrompt("");
      await load();
      toast.success(`Created version v${res.script.versions.length}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Iterate failed");
    } finally {
      setWorking(false);
    }
  };

  const current = script?.versions?.[versionIdx];

  return (
    <div className="flex h-full" data-testid="scripts-panel">
      {/* List */}
      <aside className="w-72 border-r border-[#2D7544] flex flex-col shrink-0 bg-[#050805]">
        <div className="px-4 py-3 border-b border-[#2D7544] flex items-center gap-2">
          <Library size={14} />
          <div className="dv-overline">Saved Scripts</div>
        </div>
        <div className="px-4 py-2 border-b border-[#2D7544] flex items-center gap-2">
          <Search size={13} className="text-[#66B07A]" />
          <input
            data-testid="scripts-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter scripts…"
            className="w-full text-sm bg-transparent py-1.5 focus:outline-none placeholder:text-[#3D8657]"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" data-testid="scripts-list">
          {items.length === 0 ? (
            <div className="border border-dashed border-[#2D7544] p-6 text-center text-xs text-[#66B07A]">
              No saved scripts yet. Generate one in <span className="font-bold">Script Lab</span>, then click <span className="font-bold">Save to library</span>.
            </div>
          ) : (
            items.map((s) => (
              <div
                key={s.id}
                data-testid={`script-item-${s.id}`}
                onClick={() => setActiveId(s.id)}
                className={`border border-[#2D7544] p-3 cursor-pointer transition-all ${
                  activeId === s.id ? "bg-[#00FF41] text-[#000000]" : "bg-[#050805] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#00FF41]"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileCode size={13} />
                      <div className="font-bold text-sm truncate">{s.name}</div>
                    </div>
                    <div className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${activeId === s.id ? "text-[#3D8657]" : "text-[#66B07A]"}`}>
                      {s.language} · v{s.latest_version}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={(e) => rename(e, s)}
                      data-testid={`rename-script-${s.id}`}
                      className={`p-1 border border-transparent ${activeId === s.id ? "hover:border-[#00FF41]" : "hover:border-[#2D7544] hover:bg-[#0D1F12]"}`}
                      title="Rename"
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      onClick={(e) => remove(e, s)}
                      data-testid={`delete-script-${s.id}`}
                      className={`p-1 border border-transparent ${activeId === s.id ? "hover:border-[#00FF41]" : "hover:border-[#FF3344] hover:text-[#FF3344]"}`}
                      title="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Detail */}
      <section className="flex-1 flex flex-col bg-[#000000] overflow-hidden">
        {!script ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" data-testid="scripts-empty">
            <Library size={32} className="text-[#00FF41] mb-3" />
            <div className="font-heading text-3xl font-extrabold tracking-tighter">Versioned script library</div>
            <p className="text-sm text-[#66B07A] mt-3 max-w-md leading-relaxed">
              Save generated scripts from Script Lab here. Each save creates v1; ask Claude to revise it and a new version is appended automatically.
            </p>
          </div>
        ) : (
          <>
            <div className="border-b border-[#2D7544] px-6 py-4 flex items-end justify-between gap-4 bg-[#050805]">
              <div className="min-w-0">
                <div className="dv-overline mb-1">Script · {script.language}</div>
                <h3 className="font-heading text-2xl font-extrabold tracking-tighter truncate">{script.name}</h3>
              </div>
              <div className="flex items-center gap-2">
                <label className="dv-overline flex items-center gap-2">
                  <Clock size={12} /> Version
                  <select
                    data-testid="version-select"
                    value={versionIdx}
                    onChange={(e) => setVersionIdx(parseInt(e.target.value, 10))}
                    className="dv-input py-1 text-xs font-mono"
                  >
                    {script.versions.map((v, i) => (
                      <option key={v.version} value={i}>{`v${v.version}`}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {/* Viewer */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
              {current?.prompt && (
                <div className="bg-[#050805] border border-[#2D7544] p-4 text-sm">
                  <div className="dv-overline mb-1">Prompt for v{current.version}</div>
                  <div className="whitespace-pre-wrap leading-relaxed">{current.prompt}</div>
                </div>
              )}

              {script.language === "cxalloy" ? (
                <CxAlloyVersionView
                  script={script}
                  current={current}
                  selectedDocs={selectedDocs}
                  onChanged={async () => {
                    const fresh = await api.getScript(script.id);
                    setScript(fresh);
                    setVersionIdx(fresh.versions.length - 1);
                    await load();
                  }}
                />
              ) : (
                <GenericCodeVersionView script={script} current={current} />
              )}

              {current?.notes && current.notes !== current.code && (
                <div className="bg-[#050805] border-l-4 border-[#00FFC9] border border-[#2D7544] p-4 text-sm whitespace-pre-wrap leading-relaxed">
                  <div className="dv-overline mb-1">Notes</div>
                  {current.notes}
                </div>
              )}
            </div>

            {/* Iterate */}
            <div className="border-t border-[#2D7544] bg-[#050805] p-4 pr-44">
              <div className="dv-overline mb-2 flex items-center gap-2">
                <GitBranch size={12} /> Iterate · creates v{(script.versions.at(-1)?.version || 0) + 1}
              </div>
              <div className="flex gap-2 items-end">
                <textarea
                  data-testid="iterate-input"
                  value={iteratePrompt}
                  onChange={(e) => setIteratePrompt(e.target.value)}
                  placeholder="e.g. Add error handling and convert to async"
                  rows={2}
                  className="dv-input flex-1 resize-none min-h-[64px]"
                />
                <button
                  data-testid="iterate-btn"
                  onClick={iterate}
                  disabled={working || !iteratePrompt.trim()}
                  className="dv-btn-primary h-[64px] px-5 flex items-center gap-2"
                >
                  {working ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Iterate
                </button>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A] mt-2">
                Context: {selectedDocs.length} doc{selectedDocs.length === 1 ? "" : "s"} selected · falls back to previous version's docs
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function extOf(l) {
  const x = (l || "").toLowerCase();
  return {
    python: "py", javascript: "js", typescript: "ts", bash: "sh", shell: "sh",
    sql: "sql", go: "go", rust: "rs", java: "java", powershell: "ps1", json: "json", yaml: "yaml", yml: "yml",
  }[x] || "txt";
}

function GenericCodeVersionView({ script, current }) {
  return (
    <div className="border border-[#2D7544] bg-[#050805] text-[#00FF41] font-mono text-xs overflow-hidden" data-testid="script-code-block">
      <div className="flex justify-between items-center px-3 py-2 border-b border-[#1F4D2A] text-[#66B07A] text-[10px] uppercase tracking-widest">
        <span>{current?.language || "code"} · v{current?.version}</span>
        <div className="flex items-center gap-1">
          <button
            data-testid="script-copy-btn"
            onClick={() => {
              navigator.clipboard.writeText(current?.code || "");
              toast.success("Copied");
            }}
            className="px-2 py-1 hover:bg-[#0D1F12] flex items-center gap-1"
          >
            <Copy size={11} /> Copy
          </button>
          <button
            data-testid="script-download-btn"
            onClick={() => {
              const ext = extOf(current?.language);
              const blob = new Blob([current?.code || ""], { type: "text/plain" });
              downloadBlob(blob, `${safeName(script.name)}.v${current?.version}.${ext}`);
            }}
            className="px-2 py-1 hover:bg-[#0D1F12] flex items-center gap-1"
          >
            <Download size={11} /> Download
          </button>
        </div>
      </div>
      <pre className="p-4 overflow-x-auto whitespace-pre"><code>{current?.code}</code></pre>
    </div>
  );
}

function CxAlloyVersionView({ script, current, selectedDocs, onChanged }) {
  const [spec, setSpec] = useState(null);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      const parsed = current?.code ? JSON.parse(current.code) : null;
      setSpec(parsed);
      if (parsed) {
        api.cxalloyValidate(parsed).then(setValidation).catch(() => setValidation(null));
      }
    } catch (e) {
      setSpec(null);
    }
  }, [current?.code]);

  if (!spec) {
    return (
      <div className="bg-[#050805] border border-[#FF3344] p-4 text-sm">
        <div className="dv-overline mb-1 text-[#FF3344]">CxAlloy spec corrupted</div>
        <pre className="font-mono text-xs whitespace-pre-wrap">{current?.code}</pre>
      </div>
    );
  }

  const download = async (format) => {
    const hasErrors = validation?.summary?.errors > 0;
    if (hasErrors) {
      const ok = await confirmDialog({
        title: "Export with validation errors?",
        message: `This template has ${validation.summary.errors} error(s). Consider running Auto-fix first.`,
        confirmLabel: "Download anyway",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      const { url, filename } = await api.cxalloyExportLink(spec, format, hasErrors);
      downloadViaUrl(url, filename);
      toast.success(`Downloading ${filename}`);
    } catch {
      toast.error("Download failed");
    }
  };

  const autofix = async () => {
    setBusy(true);
    try {
      const res = await api.cxalloyAutofix(spec);
      // Save as new version so history is preserved
      await api.addScriptVersion(script.id, {
        code: JSON.stringify(res.spec),
        language: "cxalloy",
        prompt: `Auto-fix: ${(res.applied || []).join("; ")}`,
        document_ids: selectedDocs || current?.document_ids || [],
      });
      const n = (res.applied || []).length;
      toast.success(`Auto-fixed (${n} change${n === 1 ? "" : "s"}) — saved as new version`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="cxalloy-version-view">
      <div className="dv-overline mb-2 flex items-center gap-2">
        <ClipboardList size={12} /> CxAlloy Template · v{current.version}
      </div>
      <SpecPreview
        spec={spec}
        validation={validation}
        onDownload={download}
        onAutofix={autofix}
        working={busy}
      />
    </div>
  );
}

